// Resolution order for which facility the Edit Farm screen should display on
// mount / reload. Encodes the fix for v2.1 finding #29 (Edit Farm silently
// switches to the first farm in the list after reload). Pure helper so the
// contract is unit-tested without mounting the screen.
//
// Source priority (first non-empty wins):
//   1. `explicitPreferred` — caller passed an explicit id (e.g. after a save).
//   2. `urlFacilityId` — `?facilityId=<id>` from the current URL.
//   3. `activeContextFarmId` — tenant-scoped active farm from
//      `loadActiveContextSelection().farmId`, the same key Biomass /
//      Database / Feed honor.
//   4. `inMemorySelectedId` — whatever the component had selected before this
//      reload (covers in-session farm picker clicks, not browser reloads).
// Only when all four are empty (or none of them match a validated farm) does
// the resolver fall back to `firstFarmId`, and it flags `fellBackToFirst` so
// the UI can surface a banner explaining the demotion.

export type ResolvePreferredFarmInput = {
  validatedFarmIds: ReadonlyArray<string>
  explicitPreferred?: string | null
  urlFacilityId?: string | null
  activeContextFarmId?: string | null
  inMemorySelectedId?: string | null
}

export type ResolvedPreferredFarm = {
  preferredFarmId: string | null
  source: 'explicit' | 'url' | 'activeContext' | 'inMemory' | 'firstFallback' | 'empty'
  fellBackToFirst: boolean
}

const sanitize = (value: string | null | undefined): string => {
  if (typeof value !== 'string') return ''
  return value.trim()
}

const matches = (candidate: string, validated: ReadonlyArray<string>): boolean =>
  Boolean(candidate) && validated.includes(candidate)

export const resolveEditFarmPreferredFarm = (
  input: ResolvePreferredFarmInput
): ResolvedPreferredFarm => {
  const validated = input.validatedFarmIds.map(sanitize).filter(Boolean)
  if (validated.length === 0) {
    return { preferredFarmId: null, source: 'empty', fellBackToFirst: false }
  }

  const candidates: Array<{ id: string; source: ResolvedPreferredFarm['source'] }> = [
    { id: sanitize(input.explicitPreferred), source: 'explicit' },
    { id: sanitize(input.urlFacilityId), source: 'url' },
    { id: sanitize(input.activeContextFarmId), source: 'activeContext' },
    { id: sanitize(input.inMemorySelectedId), source: 'inMemory' }
  ]

  for (const candidate of candidates) {
    if (matches(candidate.id, validated)) {
      return { preferredFarmId: candidate.id, source: candidate.source, fellBackToFirst: false }
    }
  }

  return {
    preferredFarmId: validated[0],
    source: 'firstFallback',
    fellBackToFirst: true
  }
}
